/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { styles } from './themes/PlotAreaStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { ChartPlotArea } from './PlotArea.types';
import { Scale } from '../UNSAFE_Chart';
import { useColorScheme } from '../hooks/UNSAFE_useColorScheme';

type Props = {
  xScale: Scale;
  yScale: Scale;
  xViewportMin: number;
  xViewportMax: number;
  isLog: boolean;
  yTicks: number[];
  yMinorTicks: number[];
  plotArea?: ChartPlotArea;
  yAxisPosition: 'left' | 'right' | 'top' | 'bottom';
};

const TICK_DEFAULT = {
  yMajorTick: {
    isRendered: true
  },
  yMinorTick: {
    isRendered: false
  },
  xMajorTick: {
    isRendered: false
  }
};

export function Gridlines({
  plotArea = TICK_DEFAULT,
  yMinorTicks,
  xScale,
  yScale,
  isLog,
  xViewportMin,
  xViewportMax,
  yTicks,
  yAxisPosition
}: Props) {
  const isHoriz = yAxisPosition === 'top' || yAxisPosition === 'bottom';
  const [x1, x2] = xScale.range();
  const [y1, y2] = yScale.range();

  let yLinesPath = '';
  let xLinesPath = '';
  let yMinorPath = '';
  const { yMajorTick: _yMajorTick, yMinorTick: _yMinorTick, xMajorTick: _xMajorTick } = plotArea;
  const yMajorTick = { ...TICK_DEFAULT.yMajorTick, ..._yMajorTick };
  const yMinorTick = { ...TICK_DEFAULT.yMinorTick, ..._yMinorTick };
  const xMajorTick = { ...TICK_DEFAULT.xMajorTick, ..._xMajorTick };
  const isYMinorRendered = yMinorTick?.isRendered === true || isLog;

  if (yMajorTick?.isRendered) {
    for (let i = 1; i < yTicks.length; i++) {
      const y = yScale.transform(yTicks[i]);
      yLinesPath += isHoriz ? `M${y},${x1}L${y},${x2}` : `M${x1},${y}L${x2},${y}`;
    }
  }

  if (xMajorTick?.isRendered) {
    const xStartIndex = Math.floor(xViewportMin - 0.5) + 1.5;
    for (let i = xStartIndex; i < xViewportMax; i++) {
      const x = xScale.transform(i);
      xLinesPath += isHoriz ? `M${y1},${x}L${y2},${x}` : `M${x},${y1}L${x},${y2}`;
    }
  }

  if (isYMinorRendered) {
    for (let i = 0; i < yMinorTicks.length; i++) {
      const y = yScale.transform(yMinorTicks[i]);
      yMinorPath += isHoriz ? `M${y},${x1}L${y},${x2}` : `M${x1},${y}L${x2},${y}`;
    }
  }

  const colorScheme = useColorScheme();

  return (
    <>
      {yMajorTick?.isRendered && (
        <path
          d={yLinesPath}
          class={classNames([
            styles.noPointerEvents,
            styles.majorTickLineStyles,
            yMajorTick?.lineStyle === 'dashed' ? styles.dashedLine : undefined,
            yMajorTick?.lineStyle === 'dotted' ? styles.dottedLine : undefined
          ])}
          style={{
            stroke: yMajorTick.lineColor,
            strokeWidth: yMajorTick.lineWidth
          }}
        />
      )}
      {xMajorTick?.isRendered && (
        <path
          d={xLinesPath}
          class={classNames([
            styles.noPointerEvents,
            styles.majorTickLineStyles,
            xMajorTick?.lineStyle === 'dashed' ? styles.dashedLine : undefined,
            xMajorTick?.lineStyle === 'dotted' ? styles.dottedLine : undefined
          ])}
          style={{
            stroke: xMajorTick.lineColor,
            strokeWidth: xMajorTick.lineWidth
          }}
        />
      )}
      {isYMinorRendered && (
        <path
          d={yMinorPath}
          class={classNames([
            styles.noPointerEvents,
            styles.minorTickLineStyles,
            colorScheme === 'light'
              ? styles.lightMinorTickLineStroke
              : styles.darkMinorTickLineStroke,
            yMinorTick?.lineStyle === 'dashed' ? styles.dashedLine : undefined,
            yMinorTick?.lineStyle === 'dotted' ? styles.dottedLine : undefined
          ])}
          style={{
            stroke: yMinorTick?.lineColor,
            strokeWidth: yMinorTick?.lineWidth
          }}
        />
      )}
    </>
  );
}
